import clsx from 'clsx';
import * as React from 'react';
import { Input } from 'react-aria-components';

export type AutoSizeInputProps = {
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    /**
     * Class name for the input element.
     */
    inputClassName?: string;
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange'>;

/**
 * Auto size input component.
 * @example
 * <AutoSizeInput value={value} onChange={onChange} style={{ maxWidth: 200, minWidth: 50 }} />
 */
export const AutoSizeInput = React.forwardRef(function AutoSizeInput(
    props: AutoSizeInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
) {
    const { className, inputClassName, style, ...rest } = props;

    const [minWidth, setMinWidth] = React.useState(0);
    const placeholderRef = React.useRef<HTMLDivElement>(null);
    React.useLayoutEffect(() => {
        if (placeholderRef.current) {
            setMinWidth(placeholderRef.current.offsetWidth);
        }
    }, [props.placeholder]);

    return (
        <div
            className={clsx('relative inline-block overflow-hidden', className)}
            style={{
                ...style,
                position: 'relative',
                minWidth: style?.minWidth ?? minWidth,
            }}
        >
            <div
                aria-hidden
                className="pointer-events-none relative select-none"
                style={{ height: '1lh' }}
            >
                <InvisibleInlineText>{props.value}</InvisibleInlineText> <br />
                <InvisibleInlineText ref={placeholderRef}>
                    {props.placeholder ?? ''}
                </InvisibleInlineText>
            </div>
            <Input
                ref={ref}
                className={clsx(
                    'absolute top-0 left-0 size-full text-ellipsis bg-transparent',
                    inputClassName
                )}
                style={{
                    color: 'inherit',
                    padding: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                }}
                {...rest}
            />
        </div>
    );
});

const InvisibleInlineText = React.forwardRef(function InvisibleInlineText(
    props: { children: string },
    ref: React.Ref<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            className="pointer-events-none relative inline-block h-0 min-w-0.5 select-none text-ellipsis whitespace-pre align-top opacity-0"
            style={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
                letterSpacing: 'inherit',
            }}
        >
            {props.children}
        </div>
    );
});
